import { useRef, useState } from "react";
import { ArrowRight, Shield, Globe, Zap } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * FCUPreview
 *
 * Spectacular "preview the credit union" showcase that uses the two
 * official photo mockups:
 *   - /brand/tdspfcu-signage.jpg   (evening lit glass storefront sign)
 *   - /brand/tdspfcu-cards.jpg     (daytime storefront + VISA debit cards)
 *
 * NCUA-compliant: everything is framed as "preview / proposed / contingent on
 * charter approval" — no claims of operational banking services.
 */

function TiltCard({ children, className = "", intensity = 10 }) {
  const ref = useRef(null);
  const [t, setT] = useState({ rx: 0, ry: 0, sx: 0, sy: 0 });

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const ry = ((x - cx) / cx) * intensity;
    const rx = -((y - cy) / cy) * intensity;
    setT({ rx, ry, sx: (x / rect.width) * 100, sy: (y / rect.height) * 100 });
  };
  const onLeave = () => setT({ rx: 0, ry: 0, sx: 50, sy: 50 });

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ perspective: 1200 }}
      className={className}
    >
      <div
        style={{
          transform: `rotateX(${t.rx}deg) rotateY(${t.ry}deg)`,
          transition: "transform 300ms cubic-bezier(0.4,0,0.2,1)",
          transformStyle: "preserve-3d",
        }}
        className="relative"
      >
        {children}
        {/* glare */}
        <div
          className="pointer-events-none absolute inset-0 rounded-[inherit] mix-blend-overlay opacity-60"
          style={{
            background: `radial-gradient(circle at ${t.sx}% ${t.sy}%, rgba(255,255,255,0.45), transparent 55%)`,
            transition: "background 120ms linear",
          }}
        />
      </div>
    </div>
  );
}

const FEATURES = [
  {
    icon: Shield,
    title: "Member-Owned, Not Shareholder-Owned",
    body:
      "Every supporter who joins on approval becomes a 1-vote owner. Profits circulate back into dividends, lower loan rates, and community investment.",
  },
  {
    icon: Globe,
    title: "Field Of Membership",
    body:
      "Fulton County · DeKalb County · City of College Park. Live, auditable, and bundled into the NCUA Field-Of-Membership Study.",
  },
  {
    icon: Zap,
    title: "Debit · Mobile · Same-Day Transfers (On Approval)",
    body:
      "Proposed services: TDSP FCN VISA debit, mobile deposit, same-day ACH, bill-pay, business accounts, car loans and first-time home down-payment loans.",
  },
];

export default function FCUPreview() {
  return (
    <section
      className="relative overflow-hidden border-y border-[#C0C0C0]/20"
      data-testid="fcu-preview-section"
      style={{
        background:
          "linear-gradient(180deg, #050505 0%, #0a0a0a 50%, #050505 100%)",
      }}
    >
      {/* ambient silver glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(1400px 600px at 50% 0%, rgba(224,224,224,0.1) 0%, rgba(0,0,0,0) 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-24 lg:py-32">
        {/* Headline */}
        <div className="text-center max-w-4xl mx-auto">
          <div
            className="label-eyebrow inline-flex items-center gap-2"
            style={{ color: "rgba(224,224,224,0.9)" }}
          >
            <span className="inline-block w-6 h-px bg-[#C0C0C0]" />
            Proposed Branch · Preview · Pending NCUA Approval
            <span className="inline-block w-6 h-px bg-[#C0C0C0]" />
          </div>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-bdu-cream leading-[1.05] tracking-tight">
            A{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(135deg, #FFFFFF 0%, #D8D8D8 40%, #808080 100%)",
              }}
            >
              real building. Real cards.
            </span>
            <br />
            Real wealth, held{" "}
            <span className="italic text-[#E0E0E0]">by us,</span> for us.
          </h2>
          <p className="mt-6 text-bdu-cream/70 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Here is a photorealistic preview of the proposed{" "}
            <span className="text-[#E0E0E0] font-semibold">TDSP FCN</span>{" "}
            branch headquarters and member VISA debit card — minted the moment
            the NCUA grants our charter.
          </p>
        </div>

        {/* Showcase grid: Branch Signage + Debit Card */}
        <div className="mt-16 grid lg:grid-cols-12 gap-10 items-center">
          {/* LEFT — Branch signage (evening lit) */}
          <div className="lg:col-span-7">
            <TiltCard
              intensity={6}
              className="group"
            >
              <div className="relative">
                {/* silver chrome frame */}
                <div
                  className="absolute -inset-[3px] rounded-sm"
                  style={{
                    background:
                      "linear-gradient(135deg, #F5F5F5 0%, #808080 30%, #C0C0C0 50%, #4A4A4A 70%, #E0E0E0 100%)",
                    filter: "drop-shadow(0 30px 60px rgba(224,224,224,0.25))",
                  }}
                />
                <div className="relative overflow-hidden rounded-sm">
                  <img
                    src="/brand/tdspfcu-signage.jpg"
                    alt="TDSP FCU — proposed branch headquarters signage preview (evening)"
                    className="w-full h-auto block"
                    data-testid="fcu-preview-signage-img"
                  />
                  {/* subtle corner vignette */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.45) 100%)",
                    }}
                  />
                  {/* caption badge */}
                  <div className="absolute bottom-4 left-4 bg-black/70 backdrop-blur-sm border border-[#C0C0C0]/40 px-4 py-2">
                    <div className="text-[0.55rem] uppercase tracking-[0.25em] text-[#C0C0C0]">
                      Preview Rendering
                    </div>
                    <div className="font-serif text-sm text-bdu-cream mt-0.5">
                      Branch HQ · 100 Trillion Way
                    </div>
                  </div>
                </div>
              </div>
            </TiltCard>
          </div>

          {/* RIGHT — Supporting cards: feature pillars */}
          <div className="lg:col-span-5 space-y-5">
            {FEATURES.map(({ icon: Icon, title, body }, i) => (
              <div
                key={title}
                className="relative bg-black/60 backdrop-blur-sm border border-[#C0C0C0]/25 p-6 hover:border-[#C0C0C0]/60 hover:-translate-y-0.5 transition"
                data-testid={`fcu-preview-feature-${i}`}
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <div
                  className="w-10 h-10 rounded-full border border-[#C0C0C0]/40 flex items-center justify-center"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(224,224,224,0.15), rgba(192,192,192,0.05))",
                  }}
                >
                  <Icon size={18} className="text-[#E0E0E0]" />
                </div>
                <div className="mt-4 font-serif text-lg text-bdu-cream leading-snug">
                  {title}
                </div>
                <p className="mt-2 text-sm text-bdu-cream/65 leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* BIG daytime + VISA debit card hero image */}
        <div className="mt-20">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div
              className="label-eyebrow"
              style={{ color: "rgba(224,224,224,0.85)" }}
            >
              The Founder's Card · Minted On Charter Approval
            </div>
            <h3 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-bdu-cream leading-tight">
              Every pledge card today becomes a{" "}
              <span
                className="bg-clip-text text-transparent italic"
                style={{
                  backgroundImage:
                    "linear-gradient(135deg, #FFFFFF 0%, #C0C0C0 60%, #808080 100%)",
                }}
              >
                real VISA debit card
              </span>{" "}
              tomorrow.
            </h3>
          </div>

          <TiltCard intensity={4} className="group">
            <div className="relative">
              <div
                className="absolute -inset-[2px] rounded-sm"
                style={{
                  background:
                    "linear-gradient(135deg, #E0E0E0 0%, #4A4A4A 30%, #FFFFFF 50%, #808080 70%, #D8D8D8 100%)",
                  filter: "drop-shadow(0 40px 80px rgba(224,224,224,0.3))",
                }}
              />
              <div className="relative overflow-hidden rounded-sm">
                <img
                  src="/brand/tdspfcu-cards.jpg"
                  alt="TDSP FCN proposed branch with VISA debit card featuring Greg L Bradford, Founder CEO President"
                  className="w-full h-auto block"
                  data-testid="fcu-preview-cards-img"
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.6) 100%)",
                  }}
                />
                {/* floating stat chips over the hero */}
                <div className="absolute left-6 bottom-6 right-6 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="text-[0.55rem] uppercase tracking-[0.25em] text-[#C0C0C0]">
                      Preview Rendering · Not In Circulation
                    </div>
                    <div className="font-serif text-xl sm:text-2xl text-bdu-cream mt-1">
                      TDSP FCN · VISA Debit · Member Preview
                    </div>
                  </div>
                  <a
                    href="#subscribe"
                    className="inline-flex items-center gap-2 px-6 py-3 uppercase tracking-widest text-[0.65rem] font-bold text-black transition-all hover:brightness-110 shadow-[0_10px_30px_-5px_rgba(224,224,224,0.5)]"
                    style={{
                      background:
                        "linear-gradient(135deg, #FFFFFF 0%, #D8D8D8 40%, #B0B0B0 100%)",
                      border: "1px solid #B0B0B0",
                    }}
                    data-testid="fcu-preview-card-cta"
                  >
                    Reserve My Member Card <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            </div>
          </TiltCard>
        </div>

        {/* bottom CTA bar */}
        <div className="mt-16 text-center">
          <p className="text-bdu-cream/60 text-sm max-w-2xl mx-auto leading-relaxed">
            Images shown are photorealistic branding previews of the proposed credit union. No deposit accounts, loan products,
            or VISA cards are yet available for issuance. Full services launch{" "}
            <span className="text-[#E0E0E0]">upon NCUA charter approval.</span>
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/field-of-membership"
              className="btn-ghost"
              data-testid="fcu-preview-fom-cta"
            >
              Check My Field Of Membership
            </Link>
            <a
              href="#subscribe"
              className="inline-flex items-center gap-2 px-7 py-3.5 uppercase tracking-widest text-xs font-bold text-black transition-all hover:brightness-110 shadow-[0_14px_40px_-10px_rgba(224,224,224,0.55)]"
              style={{
                background:
                  "linear-gradient(135deg, #FFFFFF 0%, #D8D8D8 40%, #B0B0B0 100%)",
                border: "1px solid #B0B0B0",
              }}
              data-testid="fcu-preview-pledge-cta"
            >
              Sign My Pledge Card <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
